import React, { useRef, useState } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { Card, Col, Row } from 'react-bootstrap';
import { DirectionalHint, Dropdown, IconButton, Label, Stack, TooltipHost, TooltipOverflowMode, mergeStyleSets } from '@fluentui/react';
import { useTranslation } from 'react-i18next';
import CLabel from '../../../components/Common/CLabel';
import ToolBar from '../../../components/Common/Toolbar';
import SidePanelModifyContainer from './SidePanelModifyContainer';
import { isNullUndefinedOrEmpty, showMessage } from '../../../common/Helpers';
import SidePanelConfigure from './SidePanelConfigure';
import { TeachBubbleMultiSteps } from '../../../components/Templates/TeachBubble/indexMultiSteps';
import MessageBox from '../../../components/Common/Message/MessageBox';
import { __AppElementType, __MessageTypes, __enum_TeachBoardConfig, enum__BOARD_TEMPLATE_TYPE, enum__ComponentType } from '../../../common/Dictionaries';
import { __onGoToBoard } from '../../../common/SystemRedirections';
import './style.css'
import { addBoardTemplate, canTemplateCreation } from '../../../services/sKPI/board.service';
import { action_setBoardConfigTemplateProperty, action_setBoardConfigTemplateTypeList } from '../../../reducers/actions/KPI/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useGetTemplateTypeForBoardConfig } from './useOfBoardConfig';
import CValidator from '../../../components/Common/CValidator';
import { css } from '@uifabric/experiments/lib/Utilities';
import moment from 'moment';
import INFO_COMMENT from '../../../components/Products/sKPI/Board/_ComponentTypes/INFO_COMMENT';
import ProfileVerify from '../../../components/Templates/Site/ProfileVerify';
import AuthVerify from '../../../components/Templates/Site/AuthVerify';

function BoardConfigurationReport() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { token: currentAuth } = useSelector((state) => state.auth);

    const { _templateTypeList: templateTypeList } = useSelector((state) => state.configBoardScope);
    const { _templateConfig: templateConfig } = useSelector((state) => state.configBoardScope);

    const ResponsiveGridLayout = WidthProvider(Responsive);

    const [isPanelModifyContainerOpen, setIsPanelModifyContainerOpen] = useState(false);
    const [isPanelConfigureBoardOpen, setIsPanelConfigureBoardOpen] = useState(false);

    const [layouts, setLayouts] = useState({lg: [ {"i": "1","x": 0,"y": 0,"w": 6,"h": 4} ]});
    const [selectedContainer, setSelectedContainer] = useState(layouts.lg[0]);
    const [layoutsComponentTypes, setLayoutsComponentTypes] = useState([{i: "1", "title": "Contenedor 1", editMode: false}]);

    const [created, setCreated] = useState(false);

    const [nextId, setNextId] = useState(2);
    const [availableIds, setAvailableIds] = useState([]);

    const [messageConfig, setMessageConfig] = useState({
        message : null,
        messageType : "",
        showMessage : false,
    });

    const [isValidForm, setIsValidForm] = useState({
        templateType: true,

        title: true,
        appElementType: true,
        elementType: true,

        containersCompleted: true,
    });

    const gridLayoutRef = useRef(null);

    const isReportType = window.location.href.toLocaleLowerCase().indexOf('configuration/report') !== -1;

    const layoutConfiguration = {
        measureBeforeMount: true,
        breakpoints: { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 },
        cols: { lg: 36, md: 30, sm: 18, xs: 12, xxs: 6 },
        rowHeight: 50,
        draggableHandle: ".dragHandle",
        margin: [10, 10, 10, 10]
    }

    const classNames = mergeStyleSets({
        overflow: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
        parent: {
          selectors: { '> *:last-child': { marginTop: 10 } },
        },
    });

    useGetTemplateTypeForBoardConfig(templateTypeList);

    const removeItemFromLayout = (id) => {
        const newLayouts = { ...layouts };
        const itemIndex = newLayouts.lg.findIndex((item) => item.i === id);
        newLayouts.lg.splice(itemIndex, 1);
        setLayouts(newLayouts);

        setLayoutsComponentTypes(layoutsComponentTypes.filter(x => x.i !== id))

        setAvailableIds((prevIds) => [...prevIds, id]);
    };

    const onAddItemToLayout = () => {
        const newLayouts = { ...layouts };
        const newLayoutComponentTypes = [...layoutsComponentTypes];

        let newId;

        if (availableIds.length > 0) {
          newId = availableIds[0];
          setAvailableIds((prevIds) => prevIds.slice(1));
        } else {
          newId = nextId;
          setNextId(nextId + 1);
        }
    
        // Encuentra la última fila
        let lastRow = 0;
        newLayouts.lg.forEach((item) => {
            if (item.y > lastRow) {
                lastRow = item.y;
            }
        });
    
        // Encuentra el espacio disponible en la última fila
        let availableSpace = 36;
        newLayouts.lg.forEach((item) => {
            if (item.y === lastRow) {
                availableSpace -= item.w;
            }
        });
    
        // Calcula la posición X e Y del nuevo item
        let newItemX = 0;
        let newItemY = lastRow;
    
        if (availableSpace >= 6) {
            // Hay suficiente espacio en la fila actual
            newItemX = 36 - availableSpace;
        } else {
            // No hay suficiente espacio en la fila actual, pasa a la siguiente fila
            newItemX = 0;
            newItemY = lastRow + 5;
        }
    
        newLayoutComponentTypes.push({i: newId.toString(), title: `Contenedor ${newId}`, editMode: false})

        setLayoutsComponentTypes(newLayoutComponentTypes);

        // Agrega el nuevo item a las layouts
        newLayouts.lg.push({ i: newId.toString(), x: newItemX, y: newItemY, w: 6, h: 4 });
        setLayouts(newLayouts);
    };

    const onSaveTemplate = async () => {
        if (validateBoard()) {  
            try {
                const layoutStructure = [{
                    "title": templateConfig.title, 
                    "i":1,
                    "cols": { "lg": 36, "md": 30, "sm": 18, "xs": 12, "xxs": 6 },
                    "rowHeight": 50, 
                    "width": "100%", 
                    "isDraggable": false,
                }];
                    
                const componentStructure = [{
                    "iStructure": 1, 
                    "layouts": layouts.lg,
                    "components": layoutsComponentTypes.map(x => ({"iContainer": x.i, "componentTypeId": x.id, "componentField": x.field}))
                }]
                
                const serviceResponse = await canTemplateCreation(templateConfig.templateTypeSelected.key, templateConfig.elementSelected.id)
                
                if (serviceResponse.data.succeeded) {
                    const boardTemplate =
                    {
                        id: 0,
                        templateNemo: "",
                        templateCode: "", 
                        name: templateConfig.title,
                        creationDate: moment(Date.now()).format('MM/DD/YYYY'),
                        defaultTemplate: false,
                        boardTypeId: templateConfig.appElementSelected.key,
                        htmlTemplate: JSON.stringify(layoutConfiguration),
                        htmlStructure: JSON.stringify(layoutStructure),
                        htmlContainer: JSON.stringify(componentStructure),
                    }

                    const addTemplateServiceResponse = await addBoardTemplate(templateConfig.templateTypeSelected.key, boardTemplate, templateConfig.elementSelected.id);
                    
                    if (addTemplateServiceResponse.data.succeeded) {
                        showMessage(setMessageConfig, true, __MessageTypes.SUCCESS, t('data-lang-msg-operationperformed'))  
                        setCreated(true);
                    }
                    else {
                        showMessage(setMessageConfig, true, __MessageTypes.ERROR, t('data-lang-msg-operationerror'))
                    }
                }
                else {
                    showMessage(setMessageConfig, true, __MessageTypes.ERROR, t('data-lang-msg-alredyexist-teplatetypeforelement'))
                }
            } catch (error) {
                showMessage(setMessageConfig, true, __MessageTypes.ERROR, t('data-lang-msg-operationerror'))
            }  
        }
        else {
            showMessage(setMessageConfig, true, __MessageTypes.WARNING, t('data-lang-msg-boardconfig-worknotcomplete'))
        }
    };

    const validateBoard = () => {
        let flag = true;

        if (templateConfig.title === "" || templateConfig.title === "Nombre del tablero") {
            isValidForm.title = false;
        }
        else {
            isValidForm.title = true;
        }

        if (templateConfig.templateTypeSelected === null || templateConfig.templateTypeSelected.key === 0) {
            isValidForm.templateType = false;
        }
        else {
            isValidForm.templateType = true;
        }

        if (!isReportType) {
            if (templateConfig.elementSelected === null || templateConfig.elementSelected.key === 0) {
                isValidForm.elementType = false;
            }
            else {
                isValidForm.elementType = true;
            }

            if (templateConfig.appElementSelected === null || templateConfig.appElementSelected.key === 0) {
                isValidForm.appElementType = false;
            }
            else {
                isValidForm.appElementType = true;
            }
        }

        const containersCompleted = layoutsComponentTypes.every(obj => 
            obj.hasOwnProperty('name') && obj.hasOwnProperty('title') && obj['name'] !== ""
        );

        isValidForm.containersCompleted = containersCompleted;


        flag = flag === true ? (containersCompleted && isValidForm.title && isValidForm.templateType && isValidForm.elementType && isValidForm.appElementType) : flag;

        setIsValidForm(isValidForm);

        return flag;
    };

    const onOpenContainerModifyPanel = (container) => {
        setSelectedContainer(container)
        setIsPanelModifyContainerOpen(true);
    };

    const onOpenConfigureBoardPanel = (container) => {
        setIsPanelConfigureBoardOpen(true);
    };

    const onSetContainerData = (container, componentType, containerTitle) => {
        let componentTypes_aux = [...layoutsComponentTypes];
        let layouts_aux = [...layouts.lg];

        const componentType_aux = componentTypes_aux.find(item => item.i === container.i);
        const container_aux = layouts_aux.find(item => item.i === container.i);

        container_aux.minH = componentType.minHeigthPoints;
        container_aux.h = componentType.minHeigthPoints;
        container_aux.w = componentType.minWidthPoints;
        container_aux.minW = componentType.minWidthPoints;
        //container_aux.isResizable = false;
        
        if (!isNullUndefinedOrEmpty(componentType_aux)) {
            componentType_aux.name = componentType.name;
            componentType_aux.minHeigthPoints = componentType.minHeigthPoints;
            componentType_aux.minWidthPoints = componentType.minWidthPoints;
            componentType_aux.type = componentType.id;
            componentType_aux.title = containerTitle;
        }
        else {
            componentTypes_aux.push({i: container.i, name: componentType.name, title: containerTitle, id: componentType.id});
        }

        setLayouts({lg: layouts_aux});
        setLayoutsComponentTypes(componentTypes_aux);
    };

    const onSetBoardData = (objBoardSelected, objStructureTitle, objElementTypeSelected) => {
        dispatch(action_setBoardConfigTemplateProperty("title", objStructureTitle))
        dispatch(action_setBoardConfigTemplateProperty("elementSelected", objBoardSelected))
        dispatch(action_setBoardConfigTemplateProperty("appElementSelected", objElementTypeSelected))
    };

    
    const getComponentDummy = (layoutsComponentTypes) => {
        switch (layoutsComponentTypes.type) {
            case enum__ComponentType.INFO_COMMENT:
                return <INFO_COMMENT
                            inputParam_UseFake={true}
                            inputParam_Item={{
                                title: null,
                                comments: {
                                    value: [{
                                        comment: "Titulo",
                                        value: "Value",
                                        date: Date.now()
                                    }]
                                }
                            }}
                        />
                break;
            default:
                return undefined
        }
    }

    const generateContainer = (container) => {
        return  <Card className='h-100'>
                    <Card.Body
                        className={`p-0 m-0 text-start rounded-top ${!created ? 'dragHandle' : ''}`}
                        style={{
                                border: (!isValidForm.containersCompleted) ? (layoutsComponentTypes.find(x => x.i === container.i).title === "" || layoutsComponentTypes.find(x => x.i === container.i).name === undefined ? "1px solid red" : undefined) : undefined,
                                fontSize: "13px", fontWeight: "bolder", backgroundColor: "#e3e6f3", color: "darkblue"
                        }}
                    >
                        <Row>
                            <Col>
                                <TooltipHost
                                    content={ layoutsComponentTypes.find(x => x.i === container.i).title }
                                    overflowMode={TooltipOverflowMode.Self}
                                    hostClassName={css(classNames.overflow)}
                                >
                                    <b>{layoutsComponentTypes.find(x => x.i === container.i).title}</b>
                                </TooltipHost>
                            </Col>
                            <Col className='col-auto me-auto mt-1 report-command-bar'>
                            {
                                !created &&
                                <div id={`btnContainer${container.i}`} className="toolbar d-flex">
                                    <ToolBar
                                        overflowItems={[
                                            {
                                                key: 'editTitle',
                                                text: t('data-lang-btn-configure'),
                                                iconProps: { iconName: 'Settings' },
                                                onClick: () => onOpenContainerModifyPanel(container),
                                            }
                                        ]}
                                        display={true}
                                        styles={{root: {padding: 0, height: "15px", backgroundColor: "transparent"}}}
                                    />
                                    <ToolBar
                                        items={[
                                            {
                                                key: 'close',
                                                iconProps: { iconName: 'Clear' },
                                                onClick: () => removeItemFromLayout(container.i)
                                            }
                                        ]}
                                        display={true}
                                        styles={{icon: {fontSize: '10px'},root: {fontSize: '10px', padding: 0, height: "15px", backgroundColor: "transparent"}}}
                                    />
                                </div>
                            }
                            </Col>
                        </Row>
                    </Card.Body>
                    <Card.Body className='p-0 m-0 rounded-top kpi h-100' >
                        <Row className='h-100'>
                            <Col className={layoutsComponentTypes.find(x => x.i === container.i) !== undefined ? (layoutsComponentTypes.find(x => x.i === container.i).type === enum__ComponentType.ELEMENT_STADISTICS ? "" : "h-100 w-100 col-auto m-auto") : "h-100 w-100 col-auto m-auto"}>
                                {layoutsComponentTypes.find(x => x.i === container.i) !== undefined ? getComponentDummy(layoutsComponentTypes.find(x => x.i === container.i)) : "Not selected"}
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
    };

    const onLayoutChange = (layout) => {
        setLayouts(prevState => ({
            ...prevState,
            lg: layout
        }));
    };

    const onChangeTemplateType = (currentControl, selectedNode) => {
        setIsValidForm(prevState => ({
            ...prevState,
            elementType: true
        }));

        templateTypeList.forEach(x => {
            x.checked = false;
            if (x.key === selectedNode.key) {
                x.checked = selectedNode.checked;
            }
        })

        setIsValidForm(prevState => ({
            ...prevState,
            templateType: selectedNode.key > 0
        }));

        dispatch(action_setBoardConfigTemplateTypeList(templateTypeList));
        dispatch(action_setBoardConfigTemplateProperty("templateTypeSelected", selectedNode))
    };

    const onGoToBoard = () => {
        __onGoToBoard(5)
    };

    return (<>
            <AuthVerify currentToken={currentAuth} />
            <Card className="board-element-form h-100">
                <ProfileVerify />
                <Card.Header className="card-header-compact">
                    <Card.Title className="justify-content-between">
                        <Row>
                            <Col className='d-flex'>
                                <b>{t('data-lang-lbl-name')}: </b>
                                <div>&nbsp;{templateConfig.title !== "" ? templateConfig.title : t('data-lang-lbl-unasigned')}</div>             
                            </Col>
                            <Col className='col-auto me-auto d-flex'>
                                <div className="toolbar d-flex">
                                    {
                                        !created &&
                                        <>
                                            <Stack id='btnSelectElementType' horizontal verticalAlign="center" className='border-end pe-2'>
                                                <Row className='m-0 p-0'>
                                                    <Col className='m-0 p-0 pe-1 col-auto ms-auto' style={{fontSize: "24px"}}>
                                                        <CValidator isInValid={!isValidForm.templateType} inValidText={"*"} />
                                                    </Col>
                                                    <Col className='m-0 p-0'>
                                                        <CLabel labelText={t('data-lang-lbl-templatetype')} className="fontSizeNormal pb-2" />
                                                    </Col>
                                                    <Col className='m-0 p-0' style={{border: !isValidForm.templateType ?"1px solid red" : undefined}}>
                                                        <Dropdown
                                                            id='dropdownSelectTempleteType'
                                                            className="templateTypeDropdown"
                                                            placeholder={t('data-lang-placeholder-selectoption')}
                                                            options={ templateTypeList ? (templateTypeList.filter(x => (isReportType === false ? (x.key != enum__BOARD_TEMPLATE_TYPE.CONSOLIDATED) : (x.key === enum__BOARD_TEMPLATE_TYPE.CONSOLIDATED)))) : [] }
                                                            selectedKey={isReportType !== true ?
                                                                (templateConfig.templateTypeSelected ?
                                                                templateConfig.templateTypeSelected.key : undefined) : enum__BOARD_TEMPLATE_TYPE.CONSOLIDATED}
                                                            onChange={ onChangeTemplateType }
                                                            disabled={isReportType === true}
                                                            required={false}
                                                        />
                                                    </Col>
                                                </Row>
                                            </Stack>
                                            <div style={{border: isValidForm.title && isValidForm.elementType && isValidForm.appElementType ? undefined : "1px solid red"}}>
                                            <Stack id='btnConfigureBoard' horizontal verticalAlign="center" className='border-end pe-2'>
                                                <IconButton iconProps={{iconName: "Settings"}} onClick={() => onOpenConfigureBoardPanel()} />
                                                    <Label className="cursor-pointer" onClick={() => onOpenConfigureBoardPanel()}>
                                                        {
                                                            window.location.href.toLocaleLowerCase().indexOf('configuration/board') !== -1 ?
                                                            t('data-lang-boxname-configureboard') :
                                                            t('data-lang-boxname-configurereport')
                                                        }
                                                    </Label> 
                                            </Stack>
                                            </div>
                                            <Stack id='btnAddContainer' horizontal verticalAlign="center" className='border-end pe-2'>
                                                <IconButton iconProps={{ iconName: "Add" }} onClick={() => onAddItemToLayout()} />
                                                <Label className="cursor-pointer" onClick={() => onAddItemToLayout()}>{t('data-lang-btn-addcontainer')}</Label>
                                            </Stack>
                                            <Stack id='btnSaveBoard' horizontal verticalAlign="center" className='pe-2'>
                                                <IconButton iconProps={{ iconName: "Save" }} onClick={() => onSaveTemplate()} />
                                                <Label className="cursor-pointer" onClick={() => onSaveTemplate()}>{t('data-lang-btn-savetamplate')}</Label>
                                            </Stack>
                                            <TeachBubbleMultiSteps
                                                inputParam_ButtonId={"btnHelp"}
                                                inputParam_Title={t('data-lang-helptext-title')}
                                                inputParam_ClassName="my-auto"
                                                inputParam_ButtonClassName="pb-2"
                                                inputParam_DirectionalHint={DirectionalHint.topCenter}
                                                inputParam_Steps={__enum_TeachBoardConfig.map(x => {return t(x)})}
                                                inputParam_NextTechStepType={"BoardConfig"}
                                            />
                                        </>
                                    }
                                    {
                                        created &&
                                        <Stack id='btnAddContainer' horizontal verticalAlign="center" className='border-end pe-2'>
                                            <IconButton iconProps={{ iconName: "Go" }} onClick={() => onGoToBoard()} />
                                            <Label className="cursor-pointer" onClick={() => onGoToBoard()}>Ir a tablero</Label>
                                        </Stack> 
                                    }
                                </div>
                            </Col>
                        </Row>
                    </Card.Title>
                </Card.Header>
                <Card.Body className='pt-0' style={{minHeight: "1000px"}}>
                    <Row>
                        <Col>
                        <div style={{position: "absolute", width: "100%", right: "16px"}} className='ps-3'>
                            <MessageBox  
                                inputParam_Position='absolute'
                                inputParam_Message={ messageConfig.message }
                                inputParam_Display={ messageConfig.showMessage }
                                inputParam_MessageType={ messageConfig.messageType }
                                inputParam_SetterMessage={ setMessageConfig }
                            />
                        </div>
                        </Col>
                    </Row>
                    <Row className='pt-4'>
                        <Col>
                            <ResponsiveGridLayout
                                ref={gridLayoutRef}
                                className="layout"
                                layouts={layouts}
                                isDraggable={!created}
                                isDroppable={!created}
                                isResizable={!created}
                                autoSize={true}
                                measureBeforeMount={layoutConfiguration.measureBeforeMount}
                                breakpoints={layoutConfiguration.breakpoints}
                                cols={layoutConfiguration.cols}
                                rowHeight={layoutConfiguration.rowHeight}
                                draggableHandle={!created ? layoutConfiguration.draggableHandle : undefined}
                                margin={layoutConfiguration.margin}
                                onLayoutChange={onLayoutChange}
                            >
                                {layouts.lg.map((container) => (
                                    <div key={container.i} className="gridItem" style={
                                            {
                                                height: `${(layoutsComponentTypes.find(x => x.i === container.i) !== undefined ? layoutsComponentTypes.find(x => x.i === container.i).minHeigthPoints : container.h) * 50}px`,
                                                width: `${(layoutsComponentTypes.find(x => x.i === container.i) !== undefined ? layoutsComponentTypes.find(x => x.i === container.i).minWidthPoints : container.w) * 48.5}px`,
                                            }
                                        }
                                    >
                                        {generateContainer(container)}
                                    </div>
                                ))}
                            </ResponsiveGridLayout>
                        </Col>
                    </Row>
                </Card.Body>
                <SidePanelModifyContainer
                    inputParam_IsPanelOpen={isPanelModifyContainerOpen}
                    inputParam_SetterIsPanelOpen={setIsPanelModifyContainerOpen}
                    inputParam_Container={selectedContainer}
                    inputParam_ContainerComponentType={layoutsComponentTypes.find(x => x.i === selectedContainer.i)}
                    inputParam_SetterContainerData={onSetContainerData}
                />
                <SidePanelConfigure
                    inputParam_IsPanelOpen={isPanelConfigureBoardOpen}
                    inputParam_SetterIsPanelOpen={setIsPanelConfigureBoardOpen}
                    inputParam_StructureTitle={templateConfig.title}
                    inputParam_BoardSelected={templateConfig.elementSelected}
                    inputParam_SetterBoardData={onSetBoardData}
                />
            </Card>
        </>
    );
}

export default BoardConfigurationReport;





  